import React, { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "react-query";
import { DataTable } from "primereact/datatable";
import authApi from "../../../../service/IdApi";
import PageLoadError from "../../../../components/PageLoadError";
import PageHeading from "../../../../components/PageHeading";
import { Column } from "primereact/column";
import { Button } from 'primereact/button';
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Menu } from "primereact/menu";
import { SelectButton } from "primereact/selectbutton";
import { useAppContext } from "../../../../context/appContext";
import DataUtils from "../../../../utils/DataUtils";
import { InputSwitch } from "primereact/inputswitch";

const RENAME_KIND_OPTIONS = [
    { label: "Category", value: "category" },
    { label: "Subcategory", value: "subcategory" },
];

const FILTER_FIELDS = [
    {
        key: "categoryUri",
        label: "Category",
        placeholder: "Category",
        catalog: "categories",
        filter: true,
        showClear: true,
    },
    {
        key: "offerUri",
        label: "Offer",
        placeholder: "Offer",
        catalog: "offers",
        filter: true,
        showClear: true,
    },
    {
        key: "subcategoryUri",
        label: "Subcategory",
        placeholder: "Subcategory",
        catalog: "subcategories",
        filter: true,
        showClear: true,
    },
];

const buildCatalogFilterOptions = (entries, allLabel) => {
    const options = [{ label: allLabel, value: "" }];
    if (!Array.isArray(entries)) {
        return options;
    }
    for (let i = 0; i < entries.length; i++) {
        if (entries[i]?.uri && entries[i]?.name) {
            options.push({
                label: `${entries[i].name} (${entries[i].uri})`,
                value: entries[i].uri,
            });
        }
    }
    return options;
};

const catalogPairKey = (name, uri) => {
    return `${uri || ""}||${name || ""}`;
};

const buildEditCatalogOptions = (entries) => {
    const options = [];
    if (!Array.isArray(entries)) {
        return options;
    }
    for (let i = 0; i < entries.length; i++) {
        if (entries[i]?.uri && entries[i]?.name) {
            options.push({
                label: `${entries[i].name} (${entries[i].uri})`,
                value: catalogPairKey(entries[i].name, entries[i].uri),
                name: entries[i].name,
                uri: entries[i].uri,
            });
        }
    }
    return options;
};

const ensureEditCatalogOption = (options, name, uri) => {
    if (!name || !uri) {
        return options;
    }
    const key = catalogPairKey(name, uri);
    for (let i = 0; i < options.length; i++) {
        if (options[i].value === key) {
            return options;
        }
    }
    const next = [];
    next.push({
        label: `${name} (${uri})`,
        value: key,
        name: name,
        uri: uri,
    });
    for (let i = 0; i < options.length; i++) {
        next.push(options[i]);
    }
    return next;
};

const buildRenameSubcategoryOptions = (entries) => {
    const options = [];
    if (!Array.isArray(entries)) {
        return options;
    }
    for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        if (entry?.uri && entry?.name && entry?.parentCategoryUri) {
            const parentLabel = entry.parentCategoryName
                ? `${entry.parentCategoryName} (${entry.parentCategoryUri})`
                : entry.parentCategoryUri;
            options.push({
                label: `${entry.name} (${entry.uri}) under ${parentLabel}`,
                value: `${entry.parentCategoryUri}||${entry.uri}||${entry.name}`,
                name: entry.name,
                uri: entry.uri,
                parentCategoryUri: entry.parentCategoryUri,
                parentCategoryName: entry.parentCategoryName,
            });
        }
    }
    return options;
};

const findFilterField = (key) => {
    for (let i = 0; i < FILTER_FIELDS.length; i++) {
        if (FILTER_FIELDS[i].key === key) {
            return FILTER_FIELDS[i];
        }
    }
    return null;
};

const SuggestedOffers = () => {
    const { displayAlert } = useAppContext();
    const queryClient = useQueryClient();
    const [offers, setOffers] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const dataTable = useRef(null);
    const [showDetailsDialog, setShowDetailsDialog] = useState(false);
    const [offerTableState, setOfferTableState] = useState(() => {
        return {
            first: 0,
            rows: 25,
            page: 1,
            sortField: "n",
            sortOrder: 1,
            name: "",
            categoryUri: "",
            offerUri: "",
            subcategoryUri: "",
        };
    });
    const [nameInput, setNameInput] = useState("");
    const searchTimerRef = useRef(null);
    const [visibleFilterKeys, setVisibleFilterKeys] = useState([]);
    const [pendingOpenFilterKey, setPendingOpenFilterKey] = useState(null);
    const filterDropdownRefs = useRef({});
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [selectedOffers, setSelectedOffers] = useState([]);
    const menu = useRef(null);
    const [rowActions, setRowActions] = useState([]);
    const [reindexConfirmVisible, setReindexConfirmVisible] = useState(false);
    const [reindexAllLoading, setReindexAllLoading] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [editingOffer, setEditingOffer] = useState(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [unindexSelectedLoading, setUnindexSelectedLoading] = useState(false);
    const [isUpdateProcessing, setIsUpdateProcessing] = useState(false);
    const [showRenameDialog, setShowRenameDialog] = useState(false);
    const [renameKind, setRenameKind] = useState("category");
    const [renameTargetValue, setRenameTargetValue] = useState("");
    const [renameNewName, setRenameNewName] = useState("");
    const [renameLoading, setRenameLoading] = useState(false);

    const isFilterVisible = (key) => {
        return visibleFilterKeys.indexOf(key) >= 0;
    };

    const { data: categoryFilterOptions = [{ label: "All categories", value: "" }] } = useQuery(
        ["SuggestedOfferCategories"],
        async () => {
            const result = await authApi.get("admin/jobstock/categories");
            return buildCatalogFilterOptions(result.data?.categories, "All categories");
        },
        {
            enabled: isFilterVisible("categoryUri"),
            staleTime: 5 * 60 * 1000,
        }
    );

    const { data: offerFilterOptions = [{ label: "All offers", value: "" }] } = useQuery(
        ["SuggestedOfferOffers"],
        async () => {
            const result = await authApi.get("admin/jobstock/offers");
            return buildCatalogFilterOptions(result.data?.offers, "All offers");
        },
        {
            enabled: isFilterVisible("offerUri"),
            staleTime: 5 * 60 * 1000,
        }
    );

    const { data: subcategoryFilterOptions = [{ label: "All subcategories", value: "" }] } = useQuery(
        ["SuggestedOfferSubcategories"],
        async () => {
            const result = await authApi.get("admin/jobstock/subcategories");
            return buildCatalogFilterOptions(result.data?.subcategories, "All subcategories");
        },
        {
            enabled: isFilterVisible("subcategoryUri"),
            staleTime: 5 * 60 * 1000,
        }
    );

    const { data: editCategoryEntries = [] } = useQuery(
        ["SuggestedOfferEditCategories"],
        async () => {
            const result = await authApi.get("admin/offers/catalog/categories");
            return result.data?.categories || [];
        },
        {
            enabled: showEditDialog,
            staleTime: 0,
        }
    );

    const { data: editSubcategoryEntries = [] } = useQuery(
        ["SuggestedOfferEditSubcategories"],
        async () => {
            const result = await authApi.get("admin/offers/catalog/subcategories");
            return result.data?.subcategories || [];
        },
        {
            enabled: showEditDialog || showRenameDialog,
            staleTime: 0,
        }
    );

    const { data: renameCategoryEntries = [] } = useQuery(
        ["SuggestedOfferRenameCategories"],
        async () => {
            const result = await authApi.get("admin/offers/catalog/categories");
            return result.data?.categories || [];
        },
        {
            enabled: showRenameDialog && renameKind === "category",
            staleTime: 0,
        }
    );

    const { isLoading, isFetching, isError, refetch } = useQuery(["Offers", offerTableState], async () => {
        var body = {};
        body.first = offerTableState.first;
        body.rows = offerTableState.rows;
        body.sortField = offerTableState.sortField;
        body.sortOrder = offerTableState.sortOrder;

        if (offerTableState.name) {
            body.name = offerTableState.name;
        }
        if (offerTableState.categoryUri) {
            body.categoryUri = offerTableState.categoryUri;
        }
        if (offerTableState.offerUri) {
            body.offerUri = offerTableState.offerUri;
        }
        if (offerTableState.subcategoryUri) {
            body.subcategoryUri = offerTableState.subcategoryUri;
        }

        const result = await authApi.post("admin/offers/suggested", body);
        setOffers(result.data.offers);
        setTotalRecords(result.data.totalRecords);
        setLoaded(true);
    });

    useEffect(() => {
        if (!pendingOpenFilterKey) {
            return;
        }
        const openTimer = setTimeout(() => {
            const dropdown = filterDropdownRefs.current[pendingOpenFilterKey];
            if (dropdown && typeof dropdown.show === "function") {
                dropdown.show();
            }
            setPendingOpenFilterKey(null);
        }, 50);
        return () => {
            clearTimeout(openTimer);
        };
    }, [pendingOpenFilterKey, visibleFilterKeys]);

    useEffect(() => {
        return () => {
            if (searchTimerRef.current) {
                clearTimeout(searchTimerRef.current);
            }
        };
    }, []);

    const indexOffer = async () => {
        try {
            const result = await authApi.post("admin/offers/index", { offerId: selectedOffer._id });
            displayAlert("success", "Offer Indexed", "Offer has been indexed successfully");
            refetch();
        } catch (error) {
            console.error(error);
            displayAlert("error", "Unexpected Error", "Please try again later");
        }
    }

    const unindexOffer = async () => {
        try {
            const result = await authApi.post("admin/offers/unindex", { offerId: selectedOffer._id });
            displayAlert("success", "Offer Unindexed", "Offer has been unindexed successfully");
            refetch();
        } catch (error) {
            console.error(error);
            displayAlert("error", "Unexpected Error", "Please try again later");
        }
    }

    const reindexAll = async () => {
        try {
            setReindexAllLoading(true);
            const result = await authApi.post("admin/offers/reindex-all", {});
            displayAlert("success", "Reindex Started", "All offers were reindexed");
            refetch();
        } catch (error) {
            console.error(error);
            displayAlert("error", "Unexpected Error", "Please try again later");
        } finally {
            setReindexAllLoading(false);
        }
    }

    const openRenameDialog = () => {
        setRenameKind("category");
        setRenameTargetValue("");
        setRenameNewName("");
        setShowRenameDialog(true);
    };

    const submitRenameCatalogLabel = async () => {
        if (DataUtils.isEmpty(renameNewName)) {
            displayAlert("error", "Name is required", "Please enter the new display name");
            return;
        }
        if (DataUtils.isEmpty(renameTargetValue)) {
            displayAlert("error", "Selection required", "Please select a category or subcategory to rename");
            return;
        }

        let body = {
            kind: renameKind,
            newName: renameNewName.trim(),
        };
        if (renameKind === "category") {
            const selected = renameCategoryOptions.find((option) => option.value === renameTargetValue);
            if (!selected?.uri) {
                displayAlert("error", "Selection required", "Please select a category to rename");
                return;
            }
            body.uri = selected.uri;
        } else {
            const selected = renameSubcategoryOptions.find((option) => option.value === renameTargetValue);
            if (!selected?.uri || !selected?.parentCategoryUri) {
                displayAlert("error", "Selection required", "Please select a subcategory to rename");
                return;
            }
            body.uri = selected.uri;
            body.parentCategoryUri = selected.parentCategoryUri;
        }

        setRenameLoading(true);
        try {
            const result = await authApi.post("admin/offers/rename-catalog-label", body);
            const matchedCount = result.data?.matchedCount || 0;
            const reindexedCount = result.data?.reindexedCount || 0;
            displayAlert(
                "success",
                "Rename Complete",
                `Updated ${matchedCount} offer${matchedCount === 1 ? "" : "s"}; reindexed ${reindexedCount}.`
            );
            setShowRenameDialog(false);
            setRenameTargetValue("");
            setRenameNewName("");
            queryClient.invalidateQueries("SuggestedOfferEditCategories");
            queryClient.invalidateQueries("SuggestedOfferEditSubcategories");
            queryClient.invalidateQueries("SuggestedOfferRenameCategories");
            queryClient.invalidateQueries("SuggestedOfferCategories");
            queryClient.invalidateQueries("SuggestedOfferSubcategories");
            refetch();
        } catch (error) {
            console.error(error);
            const data = error?.response?.data;
            let message = "Please try again later";
            if (typeof data === "string" && data.trim()) {
                message = data;
            } else if (data?.msg) {
                message = data.msg;
            } else if (data?.message) {
                message = data.message;
            }
            displayAlert("error", "Rename Failed", message);
        } finally {
            setRenameLoading(false);
        }
    };

    const updateOffer = async () => {
        if (DataUtils.isEmpty(editingOffer.n)) {
            displayAlert("error", "Name is required", "Please enter a name for the offer");
            return;
        }
        if (DataUtils.isEmpty(editingOffer.c) || DataUtils.isEmpty(editingOffer.cu)) {
            displayAlert("error", "Category is required", "Please select a category for the offer");
            return;
        }
        try {
            setIsUpdateProcessing(true);
            await authApi.patch("admin/offers", editingOffer);
            displayAlert("success", "Offer Updated", "Offer has been updated successfully");
            setShowEditDialog(false);
            refetch();
        } catch (error) {
            console.error(error);
            displayAlert("error", "Unexpected Error", "Please try again later");
        } finally {
            setIsUpdateProcessing(false);
        }
    };

    const deleteOffer = async () => {
        try {
            setDeleteLoading(true);
            await authApi.delete(`admin/offers/${selectedOffer.u}`);
            displayAlert("success", "Offer Deleted", "Offer has been deleted successfully");
            setShowDeleteDialog(false);
            refetch();
        } catch (error) {
            console.error(error);
            displayAlert("error", "Unexpected Error", "Please try again later");
        } finally {
            setDeleteLoading(false);
        }
    };

    const duplicateOffer = async () => {
        try {
            var newOffer = { ...selectedOffer };
            newOffer._id = undefined;
            newOffer.n = newOffer.n + " (Copy)";
            newOffer.u = newOffer.u + "-copy";
            const result = await authApi.post("admin/offers", newOffer);
            const duplicatedOffer = result.data.offer;
            setEditingOffer(duplicatedOffer);
            setShowEditDialog(true);
            displayAlert("success", "Offer Duplicated", "You can now edit the duplicated offer");
        } catch (error) {
            console.error(error);
            displayAlert("error", "Unexpected Error", "Please try again later");
        }
    };

    const toggleUploads = async (enabled) => {
        try {
            await authApi.patch(`admin/offers/${selectedOffer._id}/uploads`,
                { enabled });

            if (selectedOffer.indexed) {
                // we need to reindex the offer
                await authApi.post("admin/offers/unindex", { offerId: selectedOffer._id });
                await authApi.post("admin/offers/index", { offerId: selectedOffer._id });
            }
            displayAlert("success", `Uploads ${enabled ? "Enabled" : "Disabled"}`,
                `Uploads have been ${enabled ? "enabled"
                    : "disabled"} for this offer`);
            refetch();
        } catch (error) {
            console.error(error);
            displayAlert("error", "Unexpected Error", "Please try again later");
        }
    };

    const unindexSelectedOffers = async () => {
        try {
            setUnindexSelectedLoading(true);
            const offerIds = selectedOffers.map(offer => offer._id);
            await authApi.post("admin/offers/unindex-multiple", { offerIds });
            displayAlert("success", "Offers Unindexed", "Selected offers have been unindexed successfully");
            refetch();
            setSelectedOffers([]);
        } catch (error) {
            console.error(error);
            displayAlert("error", "Unexpected Error", "Please try again later");
        } finally {
            setUnindexSelectedLoading(false);
        }
    };

    function hasUploadField(offer) {
        return offer?.pricingForm?.some(f => f.type === 'uploader') || false;
    }

    useEffect(() => {
        var indexMenu =
        {
            label: "Index",
            icon: "pi pi-search-plus",
            command: () => {
                indexOffer();
            },
        }
        var unindexMenu =
        {
            label: "Unindex",
            icon: "pi pi-search-minus",
            command: () => {
                unindexOffer();
            },
        }
        var editMenu =
        {
            label: "Edit",
            icon: "pi pi-pencil",
            command: () => {
                setEditingOffer({ ...selectedOffer });
                setShowEditDialog(true);
            },
        }
        var duplicateMenu =
        {
            label: "Duplicate",
            icon: "pi pi-copy",
            command: () => {
                duplicateOffer();
            },
        }
        var viewMenu =
        {
            label: "View",
            icon: "pi pi-eye",
            command: () => {
            },
        }
        var deleteMenu =
        {
            label: "Delete",
            icon: "pi pi-trash",
            command: () => {
                setShowDeleteDialog(true);
            },
        }
        var enableUploadsMenu =
        {
            label: "Enable Uploads",
            icon: "pi pi-paperclip",
            command: () => {
                toggleUploads(true);
            },
        }
        var disableUploadsMenu =
        {
            label: "Disable Uploads",
            icon: "pi pi-ban",
            command: () => {
                toggleUploads(false);
            },
        }

        var _rowActions = [];
        if (selectedOffer?.indexed) {
            _rowActions.push(unindexMenu);
        } else {
            _rowActions.push(indexMenu);
        }
        _rowActions.push(editMenu);
        _rowActions.push(duplicateMenu);
        _rowActions.push(viewMenu);
        _rowActions.push(deleteMenu);
        if (hasUploadField(selectedOffer)) {
            _rowActions.push(disableUploadsMenu);
        } else {
            _rowActions.push(enableUploadsMenu);
        }
        setRowActions(_rowActions);
    }, [selectedOffer]);

    const pageHeader = (
        <PageHeading pageTitle="Admin - Suggested Offers">
            <div className="block">
            </div>
        </PageHeading>
    );

    if (isError) {
        return (
            <div>
                {pageHeader}
                <PageLoadError />
            </div>
        );
    }

    const onPage = (event) => {
        setOfferTableState({
            ...offerTableState,
            ...event,
        });
    };

    const onSort = (event) => {
        setOfferTableState({
            ...offerTableState,
            ...event,
        });
    };

    const exportCSV = (selectionOnly) => {
        dataTable.current.exportCSV({ selectionOnly });
    };

    const keywordsTemplate = (rowData) => {
        if (!rowData.keywords || rowData.keywords.length === 0) {
            return "";
        }
        return (
            <div className="">
                {rowData.keywords.map((keyword, index) => (
                    <span key={index}>{keyword}{index < rowData.keywords.length - 1 ? ', ' : ''}</span>
                ))}
            </div>
        );
    };

    const hideDetailsDialog = () => {
        setShowDetailsDialog(false);
    }

    const applyNameFilter = (value) => {
        setOfferTableState((prev) => {
            return {
                ...prev,
                first: 0,
                page: 1,
                name: (value || "").trim(),
            };
        });
    };

    const onNameInputChange = (event) => {
        const value = event.target.value;
        setNameInput(value);
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
        }
        searchTimerRef.current = setTimeout(() => {
            applyNameFilter(value);
        }, 1000);
    };

    const clearNameFilter = () => {
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
            searchTimerRef.current = null;
        }
        setNameInput("");
        applyNameFilter("");
    };

    const clearFilters = () => {
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
            searchTimerRef.current = null;
        }
        setNameInput("");
        setVisibleFilterKeys([]);
        setOfferTableState((prev) => {
            return {
                ...prev,
                first: 0,
                page: 1,
                name: "",
                categoryUri: "",
                offerUri: "",
                subcategoryUri: "",
            };
        });
    };

    const addFilterField = (key) => {
        setVisibleFilterKeys((prev) => {
            if (prev.indexOf(key) >= 0) {
                return prev;
            }
            const next = [];
            for (let i = 0; i < prev.length; i++) {
                next.push(prev[i]);
            }
            next.push(key);
            return next;
        });
        setPendingOpenFilterKey(key);
    };

    const removeFilterField = (key) => {
        setVisibleFilterKeys((prev) => {
            const next = [];
            for (let i = 0; i < prev.length; i++) {
                if (prev[i] !== key) {
                    next.push(prev[i]);
                }
            }
            return next;
        });
        setOfferTableState((prev) => {
            return {
                ...prev,
                first: 0,
                page: 1,
                [key]: "",
            };
        });
    };

    const onFilterFieldChange = (key, event) => {
        const value = event.value == null ? "" : event.value;
        setOfferTableState({
            ...offerTableState,
            first: 0,
            page: 1,
            [key]: value,
        });
    };

    const getCatalogFilterOptions = (catalog) => {
        if (catalog === "categories") {
            return categoryFilterOptions;
        }
        if (catalog === "offers") {
            return offerFilterOptions;
        }
        if (catalog === "subcategories") {
            return subcategoryFilterOptions;
        }
        return [{ label: "All", value: "" }];
    };

    const moreFilterOptions = [];
    for (let i = 0; i < FILTER_FIELDS.length; i++) {
        const field = FILTER_FIELDS[i];
        if (visibleFilterKeys.indexOf(field.key) < 0) {
            moreFilterOptions.push({
                label: field.label,
                value: field.key,
            });
        }
    }

    const hasActiveFilters = !!(
        offerTableState.name
        || offerTableState.categoryUri
        || offerTableState.offerUri
        || offerTableState.subcategoryUri
        || nameInput
        || visibleFilterKeys.length > 0
    );

    const showActionMenu = (e, rowData) => {
        setSelectedOffer(rowData);
        menu.current.toggle(e);
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions">
                <Button
                    icon="pi pi-bars"
                    className="p-button-rounded p-button-icon-only p-button-text text-800"
                    onClick={(e) => showActionMenu(e, rowData)}
                    aria-controls="popup_menu"
                    aria-haspopup
                    tooltip="Actions"
                    tooltipOptions={{ showDelay: 1000 }}
                />
            </div>
        );
    };

    const attachmentsTemplate = (offer) => {
        let attachmentsEnabled = false;
        if (offer?.pricingForm) {
            attachmentsEnabled = offer.pricingForm.some(f => f.type === 'uploader');
        }
        return attachmentsEnabled ? 'Enabled' : 'No';
    }

    const uriTemplate = (value) => {
        if (!value) {
            return "";
        }
        return <span className="text-sm" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }}>{value}</span>;
    };

    let editCategoryOptions = buildEditCatalogOptions(editCategoryEntries);
    if (editingOffer) {
        editCategoryOptions = ensureEditCatalogOption(editCategoryOptions, editingOffer.c, editingOffer.cu);
    }
    let editSubcategoryOptions = buildEditCatalogOptions(editSubcategoryEntries);
    if (editingOffer?.s && editingOffer?.su) {
        editSubcategoryOptions = ensureEditCatalogOption(editSubcategoryOptions, editingOffer.s, editingOffer.su);
    }
    const renameCategoryOptions = buildEditCatalogOptions(renameCategoryEntries);
    const renameSubcategoryOptions = buildRenameSubcategoryOptions(editSubcategoryEntries);
    const selectedCategoryValue = editingOffer?.c && editingOffer?.cu
        ? catalogPairKey(editingOffer.c, editingOffer.cu)
        : "";
    const selectedSubcategoryValue = editingOffer?.s && editingOffer?.su
        ? catalogPairKey(editingOffer.s, editingOffer.su)
        : "";

    const onEditCategoryChange = (event) => {
        const value = event.value;
        if (!value) {
            setEditingOffer((prev) => {
                return {
                    ...prev,
                    c: "",
                    cu: "",
                };
            });
            return;
        }
        let selected = null;
        for (let i = 0; i < editCategoryOptions.length; i++) {
            if (editCategoryOptions[i].value === value) {
                selected = editCategoryOptions[i];
                break;
            }
        }
        if (!selected) {
            return;
        }
        setEditingOffer((prev) => {
            return {
                ...prev,
                c: selected.name,
                cu: selected.uri,
            };
        });
    };

    const onEditSubcategoryChange = (event) => {
        const value = event.value;
        if (!value) {
            setEditingOffer((prev) => {
                return {
                    ...prev,
                    s: null,
                    su: null,
                };
            });
            return;
        }
        let selected = null;
        for (let i = 0; i < editSubcategoryOptions.length; i++) {
            if (editSubcategoryOptions[i].value === value) {
                selected = editSubcategoryOptions[i];
                break;
            }
        }
        if (!selected) {
            return;
        }
        setEditingOffer((prev) => {
            return {
                ...prev,
                s: selected.name,
                su: selected.uri,
            };
        });
    };

    return (
        <div>
            {pageHeader}
            <div className="layout-content">
                <div className="card pt-3 sm:pt-0">
                    <div className="flex py-3 w-full flex-wrap gap-2 align-items-center justify-content-between">
                        <div className="flex flex-row flex-wrap align-items-center gap-2 flex-grow-1">
                            <div className="font-bold white-space-nowrap">
                                Filter by
                            </div>
                            <InputText
                                value={nameInput}
                                onChange={onNameInputChange}
                                placeholder="Name..."
                                aria-label="Filter by name"
                                style={{ width: "14rem", maxWidth: "100%" }}
                            />
                            {(nameInput || offerTableState.name) ? (
                                <Button
                                    type="button"
                                    icon="pi pi-times"
                                    className="p-button-outlined p-button-rounded p-button-sm"
                                    onClick={clearNameFilter}
                                    aria-label="Clear name filter"
                                    tooltip="Clear name"
                                    tooltipOptions={{ position: "top" }}
                                />
                            ) : null}
                            {visibleFilterKeys.map((key) => {
                                const field = findFilterField(key);
                                if (!field) {
                                    return null;
                                }
                                const options = getCatalogFilterOptions(field.catalog);
                                return (
                                    <div key={key} className="flex align-items-center gap-1">
                                        <Dropdown
                                            ref={(el) => {
                                                if (el) {
                                                    filterDropdownRefs.current[key] = el;
                                                } else {
                                                    delete filterDropdownRefs.current[key];
                                                }
                                            }}
                                            value={offerTableState[key]}
                                            options={options}
                                            onChange={(event) => onFilterFieldChange(key, event)}
                                            optionLabel="label"
                                            optionValue="value"
                                            placeholder={field.placeholder}
                                            filter={!!field.filter}
                                            showClear={!!field.showClear}
                                            aria-label={`Filter by ${field.label.toLowerCase()}`}
                                            style={{ minWidth: "16rem" }}
                                        />
                                        <Button
                                            type="button"
                                            icon="pi pi-times"
                                            className="p-button-text p-button-rounded p-button-sm"
                                            onClick={() => removeFilterField(key)}
                                            aria-label={`Remove ${field.label} filter`}
                                            tooltip={`Remove ${field.label}`}
                                            tooltipOptions={{ position: "top" }}
                                        />
                                    </div>
                                );
                            })}
                            {moreFilterOptions.length > 0 ? (
                                <Dropdown
                                    value=""
                                    options={moreFilterOptions}
                                    onChange={(event) => {
                                        if (event.value) {
                                            addFilterField(event.value);
                                        }
                                    }}
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder="Filter..."
                                    className="w-10rem"
                                    aria-label="Add filter"
                                />
                            ) : null}
                            {hasActiveFilters ? (
                                <Button
                                    onClick={clearFilters}
                                    className="p-button-text"
                                    icon="pi pi-times"
                                    tooltip="Clear filters"
                                    aria-label="Clear filters"
                                />
                            ) : null}
                            <Button
                                icon="pi pi-refresh"
                                className="p-button-outlined"
                                onClick={() => refetch()}
                                disabled={isLoading || isFetching}
                                aria-label="Refresh"
                                tooltip="Refresh"
                                tooltipOptions={{ position: "top" }}
                            />
                        </div>
                        <div className="flex flex-row flex-wrap justify-content-end gap-2">
                            <Button
                                type="button"
                                icon="pi pi-search-minus"
                                onClick={unindexSelectedOffers}
                                label="Unindex Selected"
                                disabled={selectedOffers.length === 0}
                                loading={unindexSelectedLoading}
                            />
                            <Button
                                type="button"
                                icon="pi pi-pencil"
                                onClick={openRenameDialog}
                                label="Rename Category/Subcategory"
                            />
                            <Button type="button" icon="pi pi-refresh" onClick={() => setReindexConfirmVisible(true)} label="Reindex All" loading={reindexAllLoading} />
                            <Button type="button" icon="pi pi-file-export" onClick={() => exportCSV(false)} data-pr-tooltip="CSV" label="Export" />
                        </div>
                    </div>
                    <Menu model={rowActions} popup ref={menu} />
                    <DataTable
                        ref={dataTable}
                        value={offers}
                        dataKey="_id"
                        selection={selectedOffers}
                        onSelectionChange={(e) => setSelectedOffers(e.value)}
                        selectionMode="multiple"
                        paginator
                        rows={offerTableState.rows}
                        rowsPerPageOptions={[25, 50, 100]}
                        className="datatable-responsive"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} suggested offers"
                        emptyMessage="No suggested offers found."
                        lazy
                        first={offerTableState.first}
                        totalRecords={totalRecords}
                        onPage={onPage}
                        onSort={onSort}
                        sortField={offerTableState.sortField}
                        sortOrder={offerTableState.sortOrder}
                        loading={isLoading || isFetching || !loaded}
                    >
                        <Column selectionMode="multiple" headerStyle={{ width: "1%", minWidth: "1rem" }}></Column>
                        <Column body={actionBodyTemplate} headerStyle={{ width: "1%", minWidth: "1rem" }}></Column>
                        <Column field="n" header="Name" sortable />
                        <Column field="u" header="Offer URI" sortable body={(rowData) => uriTemplate(rowData.u)} />
                        <Column field="c" header="Category" sortable />
                        <Column field="cu" header="Category URI" sortable body={(rowData) => uriTemplate(rowData.cu)} />
                        <Column field="s" header="Subcategory" sortable />
                        <Column field="su" header="Subcategory URI" sortable body={(rowData) => uriTemplate(rowData.su)} />
                        <Column field="indexed" header="Indexed" sortable body={(rowData) => rowData.indexed ? 'Yes' : 'No'} />
                        <Column field="p" header="Popular" sortable body={(rowData) => rowData.p ? 'Yes' : 'No'} />
                        <Column header="Attachments" sortable body={attachmentsTemplate} />
                        <Column field="keywords" header="Keywords" body={keywordsTemplate} sortable />
                    </DataTable>
                </div>
                <Dialog
                    visible={showDetailsDialog}
                    style={{ width: '50vw' }}
                    breakpoints={{ '1280px': '75vw', '641px': '95vw' }}
                    header="Offer Details"
                    modal
                    onHide={hideDetailsDialog}
                    className="p-fluid"
                >
                    {selectedOffer && (
                        <div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Name</label>
                                <div className="col">{selectedOffer.n}</div>
                            </div>
                        </div>
                    )}
                </Dialog>
                <Dialog
                    visible={reindexConfirmVisible}
                    style={{ width: '450px' }}
                    header="Confirm Reindex"
                    modal
                    footer={
                        <div>
                            <Button label="No" icon="pi pi-times" onClick={() => setReindexConfirmVisible(false)} className="p-button-text" />
                            <Button label="Yes" icon="pi pi-check" onClick={() => { setReindexConfirmVisible(false); reindexAll(); }} autoFocus />
                        </div>
                    }
                    onHide={() => setReindexConfirmVisible(false)}
                >
                    <div className="flex align-items-center justify-content-center">
                        <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                        <span>Are you sure you want to reindex all offers?</span>
                    </div>
                </Dialog>
                <Dialog
                    visible={showRenameDialog}
                    style={{ width: "520px" }}
                    header="Rename Category / Subcategory"
                    modal
                    className="p-fluid"
                    footer={
                        <div>
                            <Button
                                label="Cancel"
                                icon="pi pi-times"
                                className="p-button-text"
                                onClick={() => setShowRenameDialog(false)}
                                disabled={renameLoading}
                            />
                            <Button
                                label="Rename"
                                icon="pi pi-check"
                                onClick={submitRenameCatalogLabel}
                                loading={renameLoading}
                            />
                        </div>
                    }
                    onHide={() => {
                        if (!renameLoading) {
                            setShowRenameDialog(false);
                        }
                    }}
                >
                    <div className="flex flex-column gap-3">
                        <div className="field mb-0">
                            <label className="font-semibold">Type</label>
                            <SelectButton
                                value={renameKind}
                                options={RENAME_KIND_OPTIONS}
                                onChange={(event) => {
                                    if (event.value) {
                                        setRenameKind(event.value);
                                        setRenameTargetValue("");
                                        setRenameNewName("");
                                    }
                                }}
                                disabled={renameLoading}
                            />
                        </div>
                        <div className="field mb-0">
                            <label htmlFor="rename-target" className="font-semibold">
                                {renameKind === "category" ? "Category" : "Subcategory"}
                            </label>
                            <Dropdown
                                id="rename-target"
                                value={renameTargetValue}
                                options={renameKind === "category" ? renameCategoryOptions : renameSubcategoryOptions}
                                onChange={(event) => {
                                    const value = event.value || "";
                                    setRenameTargetValue(value);
                                    const options = renameKind === "category"
                                        ? renameCategoryOptions
                                        : renameSubcategoryOptions;
                                    let selected = null;
                                    for (let i = 0; i < options.length; i++) {
                                        if (options[i].value === value) {
                                            selected = options[i];
                                            break;
                                        }
                                    }
                                    setRenameNewName(selected?.name || "");
                                }}
                                optionLabel="label"
                                optionValue="value"
                                placeholder={renameKind === "category" ? "Select a category" : "Select a subcategory"}
                                filter
                                className="w-full"
                                disabled={renameLoading}
                            />
                        </div>
                        <div className="field mb-0">
                            <label htmlFor="rename-new-name" className="font-semibold">New display name</label>
                            <InputText
                                id="rename-new-name"
                                value={renameNewName}
                                onChange={(event) => setRenameNewName(event.target.value)}
                                disabled={renameLoading}
                            />
                            <small className="text-500">
                                URI stays the same. All matching catalog offers are updated and reindexed.
                            </small>
                        </div>
                    </div>
                </Dialog>
                <Dialog
                    visible={showEditDialog}
                    style={{ width: '450px' }}
                    header="Edit Offer"
                    modal
                    className="p-fluid"
                    footer={
                        <div>
                            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setShowEditDialog(false)} />
                            <Button label="Save" icon="pi pi-check" onClick={updateOffer} loading={isUpdateProcessing} />
                        </div>
                    }
                    onHide={() => setShowEditDialog(false)}
                >
                    {editingOffer && (
                        <div>
                            <div className="field">
                                <label htmlFor="name" className="required">Name</label>
                                <InputText
                                    id="name"
                                    value={editingOffer.n}
                                    onChange={(e) => setEditingOffer(prev => ({ ...prev, n: e.target.value }))}
                                />
                            </div>
                            <div className="field">
                                <label htmlFor="category" className="required">Category</label>
                                <Dropdown
                                    id="category"
                                    value={selectedCategoryValue}
                                    options={editCategoryOptions}
                                    onChange={onEditCategoryChange}
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder="Select a category"
                                    filter
                                    className="w-full"
                                />
                                <small className="text-color-secondary">
                                    Uses the selected category name and URI together so Typesense stays consistent.
                                </small>
                            </div>
                            <div className="field">
                                <label htmlFor="subcategory">Subcategory</label>
                                <Dropdown
                                    id="subcategory"
                                    value={selectedSubcategoryValue}
                                    options={editSubcategoryOptions}
                                    onChange={onEditSubcategoryChange}
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder="Select a subcategory"
                                    filter
                                    showClear
                                    className="w-full"
                                />
                            </div>
                            <div className="field">
                                <label htmlFor="keywords">Keywords (comma-separated)</label>
                                <InputText
                                    id="keywords"
                                    value={(editingOffer.keywords || []).join(', ')}
                                    onChange={(e) => setEditingOffer(prev => ({
                                        ...prev,
                                        keywords: e.target.value.split(',').map(k => k.trim()).filter(k => k)
                                    }))}
                                />
                            </div>
                            <div className="field">
                                <label htmlFor="popular">Popular</label>
                                <div className="text-gray-700 mb-2">Indicates that this service is popular in the category and would be defaulted to true when selecting a category and adding multiple services</div>
                                <div>
                                    <InputSwitch
                                        id="popular"
                                        checked={editingOffer.p}
                                        onChange={(e) => setEditingOffer(prev => ({ ...prev, p: e.value }))}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </Dialog>
                <Dialog
                    visible={showDeleteDialog}
                    style={{ width: '450px' }}
                    header="Confirm Delete"
                    modal
                    footer={
                        <div>
                            <Button label="No" icon="pi pi-times" onClick={() => setShowDeleteDialog(false)} className="p-button-text" />
                            <Button label="Yes" icon="pi pi-check" onClick={deleteOffer} autoFocus loading={deleteLoading} />
                        </div>
                    }
                    onHide={() => setShowDeleteDialog(false)}
                >
                    <div className="flex align-items-center justify-content-center">
                        <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                        <span>Are you sure you want to delete this offer?</span>
                    </div>
                </Dialog>
            </div>
        </div>
    );
};

export default SuggestedOffers;
